/**
 * Admin pricing browser. Read-only view of the per-country market_pricing
 * table (loaded from admin-api). For each row, surfaces the 3 pack
 * prices (in minor units + a formatted display) and a chip showing
 * whether each Stripe Price ID is provisioned.
 *
 * Edit flow intentionally out of scope until pricing strategy changes
 * post-launch — Stripe Prices are immutable, so an "edit" would have
 * to create new Prices and atomically update FKs. Until then, support
 * runs `pnpm seed:stripe-products` with an updated config when prices
 * need to change.
 */

import { COUNTRY_BILLING_CURRENCY } from '@funkihoo/shared';
import { Badge } from '@funkihoo/ui-web';
import { type PricingRow, usePricingQuery } from '../api/pricing.js';

export function PricingEditorRoute() {
  const query = usePricingQuery();

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">Pricing</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Per-country pricing for credit packs. Read-only — pricing changes ship via the
          <code className="mx-1 rounded bg-neutral-100 px-1 py-0.5 text-xs">
            seed:stripe-products
          </code>
          script (Stripe Prices are immutable; live editing here would need to create new Prices and
          atomically swap the FK rows).
        </p>
      </header>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => query.refetch()}
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          Refresh
        </button>
        {query.isFetching && <span className="text-xs text-neutral-500">refreshing…</span>}
        {query.data && (
          <span className="text-sm text-neutral-600">{query.data.rows.length} markets</span>
        )}
      </div>

      {query.isLoading && <p className="text-sm text-neutral-500">Loading pricing…</p>}
      {query.isError && (
        <p className="text-sm text-danger-600">Could not load pricing. admin-api down on :8010?</p>
      )}

      {query.data && query.data.rows.length === 0 && (
        <p className="text-sm text-neutral-500">
          No pricing rows loaded. Run <code>pnpm seed:local</code> or insert into market_pricing.
        </p>
      )}

      {query.data && query.data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-neutral-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-neutral-600">
                <th className="px-3 py-2 font-medium">Country</th>
                <th className="px-3 py-2 font-medium">Currency</th>
                <th className="px-3 py-2 font-medium">Pack 1</th>
                <th className="px-3 py-2 font-medium">Pack 10</th>
                <th className="px-3 py-2 font-medium">Pack 25</th>
                <th className="px-3 py-2 font-medium">Stripe IDs</th>
                <th className="px-3 py-2 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {query.data.rows.map((row) => (
                <PricingTableRow key={row.countryCode} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PricingTableRow({ row }: { row: PricingRow }) {
  // Display via Intl.NumberFormat. The minor-unit/major-unit conversion
  // assumes 2 decimals for most currencies — Japan/Korea/Vietnam are
  // 0-decimal but those aren't in the launch list. Worst-case display
  // would be off by 100x; the underlying Stripe charge is unaffected
  // since we send minor units directly.
  const fmt = (minor: number) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: row.currency,
        maximumFractionDigits: 2,
      }).format(minor / 100);
    } catch {
      return `${row.currency} ${(minor / 100).toFixed(2)}`;
    }
  };

  return (
    <tr className="border-b border-neutral-100">
      <td className="px-3 py-2">
        <span className="font-mono text-xs text-neutral-700">{row.countryCode}</span>
        <span className="ml-2 text-neutral-500">
          {(COUNTRY_BILLING_CURRENCY as Record<string, string | undefined>)[row.countryCode] ===
          row.currency
            ? ''
            : '⚠'}
        </span>
      </td>
      <td className="px-3 py-2">{row.currency}</td>
      <td className="px-3 py-2 font-mono">{fmt(row.pack1PriceMinor)}</td>
      <td className="px-3 py-2 font-mono">{fmt(row.pack10PriceMinor)}</td>
      <td className="px-3 py-2 font-mono">{fmt(row.pack25PriceMinor)}</td>
      <td className="px-3 py-2">
        <StripeChip label="1" present={!!row.stripePriceId1} />
        <StripeChip label="10" present={!!row.stripePriceId10} />
        <StripeChip label="25" present={!!row.stripePriceId25} />
      </td>
      <td className="px-3 py-2 text-xs text-neutral-500">
        {new Date(row.updatedAt).toLocaleDateString()}
      </td>
    </tr>
  );
}

function StripeChip({ label, present }: { label: string; present: boolean }) {
  return (
    <span title={present ? `Stripe Price ${label} provisioned` : `Stripe Price ${label} missing`}>
      <Badge variant={present ? 'success' : 'warning'} density="compact" className="mr-1">
        {label}
      </Badge>
    </span>
  );
}
